import React, { useCallback, useEffect, useState } from 'react';
import { Config } from '@leverege/plugin';
import { useDispatch, useSelector } from 'react-redux';
import { UI } from '@leverege/ui-redux';
import { Button, Dialog, FileInput, Flex, Label, Pane, Text } from '@leverege/ui-elements';
import I18N from '@leverege/i18n';
import fileUtils from "../../../forms/editors/ImagineImageEditor/fileUtils.js";
import S from './ResourceImageEditor.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('molten.imagine.forms.editors.ImagineImageEditor');
const opts = {
  table: {
    isRenderer: true,
    blockChildren: false
  },
  detail: {
    isRenderer: true,
    blockChildren: false
  }
};
export default {
  valueType: 'resourceImage',
  getOptions: usage => {
    return Config.get('ResourceImageEditor', usage, opts[usage]);
  },
  create: opts => {
    const {
      attribute,
      children,
      context,
      editable,
      eventData,
      object,
      onChange,
      usage,
      value,
      key,
      dataEditable,
      ...options
    } = opts;
    const delegate = context?.clientProps?.delegate;
    if (!editable) {
      return children;
    }
    return /*#__PURE__*/_jsx(ImageToggle, {
      usage: usage,
      delegate: delegate,
      attr: attribute,
      eventData: eventData,
      target: object,
      value: value,
      onChange: onChange,
      ...options
    }, key);
  }
};
function ImageToggle(props) {
  const {
    usage,
    attr: attribute
  } = props;
  const [opened, setOpened] = useState(false);
  const onClose = useCallback(() => {
    setOpened(false);
  }, []);
  if (usage === 'table' || usage === 'detail') {
    if (opened) {
      const titleText = attribute.source.attribute.getString('titleDialogImage', `Edit ${attribute.displayName}`);
      return /*#__PURE__*/_jsx(Dialog, {
        variant: "attributeEditorDialog|dialog",
        show: opened,
        onClose: onClose,
        title: /*#__PURE__*/_jsxs(Flex, {
          variant: "rowSpaceBetween",
          children: [/*#__PURE__*/_jsx(Text, {
            variant: "dialogTitle",
            children: titleText
          }), /*#__PURE__*/_jsx(Button, {
            variant: "closeDialog",
            onClick: onClose
          })]
        }),
        children: /*#__PURE__*/_jsx(ImagineImageEditor, {
          ...props,
          onClose: onClose
        })
      });
    }
    return /*#__PURE__*/_jsx(Button, {
      variant: "editImage",
      onClick: () => {
        setOpened(true);
      }
    });
  }
  return /*#__PURE__*/_jsx(ImagineImageEditor, {
    ...props
  });
}
function ImagineImageEditor(props) {
  const [url, setUrl] = useState();
  const {
    attr,
    eventData,
    target,
    value: pValue,
    delegate,
    onChange: pOnChange,
    disabled,
    allowDelete = true
  } = props;
  const dispatch = useDispatch();
  const value = pValue?.value?.[0] || pValue;
  const onChange = useCallback(evt => {
    if (evt.value === null) {
      pOnChange({
        data: evt.data,
        value: {
          type: 'remove',
          value: evt.value
        }
      });
    } else {
      pOnChange({
        data: evt.data,
        value: {
          type: 'set',
          value: evt.value
        }
      });
    }
  }, [pOnChange]);
  const error = useSelector(state => {
    return UI.get(state, `imageError/${attr?.blueprint?.updatePath}`);
  });
  useEffect(() => {
    setTimeout(() => {
      if (value instanceof File) {
        setUrl(URL.createObjectURL(value));
      } else if (value && target?.id) {
        const itemDelegate = delegate.item ? delegate.item(target.id) : delegate;
        dispatch(itemDelegate.resource(`resourceImage/${attr?.blueprint?.updatePath.slice(5)}`).get()).then(data => {
          const {
            data: {
              url
            }
          } = data;
          setUrl(url);
        });
      }
    }, 500);
  }, [value]);
  if (value) {
    return /*#__PURE__*/_jsxs(Pane, {
      onContextMenu: evt => {
        evt.stopPropagation();
      },
      onDoubleClick: evt => {
        evt.stopPropagation();
      },
      variant: "imageEditor|editor",
      layout: "flex:colS",
      children: [/*#__PURE__*/_jsxs(Flex, {
        variant: "rowM",
        align: "flex-start",
        className: S.container,
        children: [/*#__PURE__*/_jsx("img", {
          src: url,
          alt: "",
          className: S.image
        }), /*#__PURE__*/_jsxs(Flex, {
          variant: "colM",
          className: S.info,
          children: [/*#__PURE__*/_jsx(Label, {
            variant: "captionSmallBold",
            className: S.title,
            children: typeof value === 'string' ? value : typeof value?.name === 'string' ? value.name : null
          }), /*#__PURE__*/_jsx(Label, {
            variant: "captionSmall",
            children: value instanceof File && fileUtils.getBytesSize(value.size)
          }), /*#__PURE__*/_jsx(FileInput, {
            disabled: disabled,
            variant: "secondarySmallLeftIcon",
            eventData: eventData,
            onChange: onChange,
            fileTypes: "image/*",
            children: t('replace')
          }), allowDelete && /*#__PURE__*/_jsx(Button, {
            disabled: disabled,
            variant: "dangerSmallLeftIcon",
            onClick: () => onChange({
              value: null,
              data: eventData
            }),
            children: t('delete')
          })]
        })]
      }), error ? /*#__PURE__*/_jsx(Text, {
        variant: "inputCaptionSmallError",
        children: error
      }) : null]
    });
  }
  return /*#__PURE__*/_jsxs(Pane, {
    variant: "imageEditor|editor",
    layout: "flex:colS",
    children: [/*#__PURE__*/_jsx(FileInput, {
      disabled: disabled,
      variant: "uploadFile",
      eventData: eventData,
      onChange: onChange,
      fileTypes: "image/*",
      children: t('add')
    }), /*#__PURE__*/_jsx(Label, {
      variant: "helperText",
      children: t('caption')
    }), error ? /*#__PURE__*/_jsx(Text, {
      variant: "inputCaptionSmallError",
      children: error
    }) : null]
  });
}